import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from "@shared/store/dialog.duck";
import Dialog from '@shared/ui/molecules/Dialog';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import styled from "styled-components";
import TextField from '@shared/ui/atoms/TextField';
import { BasicUserInfo } from '@shared/models/user.models';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { findUserRequest } from '@features/settings/services/UserService';
import { naturalSort } from "@shared/utils/ArrayUtils";
import { updateDeskOwnershipsForUsers } from "@features/desks/services/DeskOwnershipService";
import { clearSelectedDeskIds, selectSelectedDeskIds } from "../ducks/manageDesks.duck";
import { successNotification } from "@shared/store/notification.duck";
import { fetchDesks } from "@features/desks/store/deskBooking.duck";
import { selectSelectedOffice } from "../../../store/officeView.duck";

export function AssignDesksToIndividualDialog() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<BasicUserInfo[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<BasicUserInfo[]>([]);
  const selectedDeskIds = useSelector(selectSelectedDeskIds);
  const selectedOffice = useSelector(selectSelectedOffice);

  useEffect(() => {
    const id = setTimeout(() => {
      findUserRequest(searchQuery)
        .then((result: BasicUserInfo[]) => naturalSort(result, 'fullName'))
        .then((result: BasicUserInfo[]) => setUsers(result));
    }, 250);

    return () => {
      clearTimeout(id);
    }
  }, [searchQuery]);

  const close = () => {
    dispatch(closeDialog())
  };

  const updateTagClick = async () => {
    setLoading(true);
    try {
      const userIds = selectedUsers.map(u => u.userId);
      await updateDeskOwnershipsForUsers(selectedDeskIds, userIds);
      dispatch(clearSelectedDeskIds());
      if (selectedOffice) {
        dispatch(fetchDesks(selectedOffice.id));
      }
      dispatch(successNotification('Desks assigned to individuals'))
      close();
    } finally {
      setLoading(false);
    }
  };

  const onChange = (val: string) => setSearchQuery(val);

  const onUserSelect = (selectedUser: BasicUserInfo) => {
    if (selectedUsers.includes(selectedUser)) {
      setSelectedUsers(selectedUsers.filter(su => su.userId !== selectedUser.userId));
    } else {
      setSelectedUsers([...selectedUsers, selectedUser]);
    }
  }

  return (
    <DialogWrapper isOpen={true} onClose={close} showLogo={true}>
      <DialogHeader title="manage-desks-sidebar.assign-desks-to-individual" />

      <TextField value={searchQuery} onChange={onChange} disabled={loading} />

      <SearchResults className={"searchDialog__results"}>
        {users.map((user: BasicUserInfo, key: number) => (
          <UserInfoRow key={key} className={'searchDialog__result'}>
            <UserAvatarAndName firstName={user.firstName} lastName={user.lastName} />
            <Checkbox value={!!selectedUsers.find(su => su.userId === user.userId)} onChange={() => onUserSelect(user)} disabled={loading} />
          </UserInfoRow>
        ))}
      </SearchResults>

      <PrimaryButton disabled={loading}
                     fullWidth={true}
                     click={updateTagClick}
                     style={{marginTop: 'auto'}}
                     text="button.save-changes" />
    </DialogWrapper>
  )
}

const DialogWrapper = styled(Dialog)<any>`
  &.dialog {
    max-width: 100%;
    min-height: 410px;
  }
  .dialog__contentContainer, .dialog__content {
    width: 500px;
    min-height: 500px;
  }
  .dialog__content {
    display: flex;
    flex-direction: column;
  }
`

const SearchResults = styled.div`
  margin-bottom: 32px;
  height: 250px;
  overflow-y: auto;
`

const UserInfoRow = styled.div<any>`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  .userAvatarAndName {
    cursor: pointer;
  }
`
